/**
 * Equipment specification sheets: lookup of the 25 installed instances in
 * equipment-specifications.json by name or subsystem, warranty and age
 * badges, the early-failure check, service history ordering and the
 * Readings tab cards from the historical store. Port of the data half of
 * spec-sheet-manager.js (no DOM); time is injected.
 */
import type { StatusColour } from '../types';
import type { HistoricalDataStore, StoredReading } from './historicalDataStore';
import type { InventoryManager, InventoryPart } from './inventory';

export interface CurrentPart {
  partNumber: string;
  serialNumber: string;
  manufacturer: string;
  model: string;
  installDate: string;
  warrantyExpires: string;
  expectedLifeYears: number;
  expectedEndOfLife: string;
}

export interface ServiceHistoryEntry {
  date: string;
  type: string;
  description: string;
  partNumber?: string;
  serialNumber?: string;
  technician?: string;
  workOrder?: string;
  results?: Record<string, unknown>;
}

export interface EquipmentSpec {
  instanceId: string;
  name: string;
  location: string;
  subsystemPath?: string;
  sensorIds?: string[];
  currentPart: CurrentPart;
  technicalSpecs?: Record<string, Record<string, unknown>>;
  operationalData?: {
    totalOperatingHours: number;
    powerCycles?: number;
    lastMaintenanceDate?: string;
    lastMaintenanceType?: string;
  };
  documents?: { type: string; name: string; url: string }[];
  serviceHistory?: ServiceHistoryEntry[];
  alternatePartNumbers?: string[];
}

/** Shape of public/data/equipment-specifications.json. */
export interface EquipmentSpecifications {
  installed_equipment: Record<string, EquipmentSpec>;
  mappings?: {
    equipment_to_inventory?: Record<string, string[]>;
    subsystem_to_instance?: Record<string, string>;
  };
}

/** findByName keyword table in lookup order (the original's object key order). */
export const NAME_KEYWORDS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['cooling', ['ACU_COOLING_FAN_01']],
  ['fan', ['ACU_COOLING_FAN_01']],
  ['acu cooling', ['ACU_COOLING_FAN_01']],
  ['lna', ['RX_LNA_01']],
  ['low noise', ['RX_LNA_01']],
  ['low_noise_amplifier', ['RX_LNA_01']],
  ['amplifier', ['RX_LNA_01', 'TX_HPA_01']],
  ['hpa', ['TX_HPA_01']],
  ['sspa', ['TX_HPA_01']],
  ['power amplifier', ['TX_HPA_01']],
  ['azimuth', ['ANT_AZ_DRIVE_01', 'ANT_AZ_BEARING_01']],
  ['elevation', ['ANT_EL_DRIVE_01', 'ANT_EL_BEARING_01']],
  ['motor', ['ANT_AZ_DRIVE_01', 'ANT_EL_DRIVE_01']],
  ['drive', ['ANT_AZ_DRIVE_01', 'ANT_EL_DRIVE_01']],
  ['drive_system', ['ANT_AZ_DRIVE_01', 'ANT_EL_DRIVE_01']],
  ['bearing', ['ANT_AZ_BEARING_01', 'ANT_EL_BEARING_01']],
  ['modem', ['MODEM_SAT_01']],
  ['satellite modem', ['MODEM_SAT_01']],
  ['raw_data_modem', ['MODEM_SAT_01']],
  ['cda_modem', ['MODEM_SAT_01']],
  ['grb_modem', ['MODEM_SAT_01']],
  ['switch', ['NET_SWITCH_01', 'SWITCH_MATRIX_01']],
  ['network', ['NET_SWITCH_01']],
  ['ethernet', ['NET_SWITCH_01']],
  ['waveguide', ['WAVEGUIDE_XBAND_01']],
  ['pressurization', ['WAVEGUIDE_XBAND_01']],
  ['freq_translator', ['FREQ_TRANS_01']],
  ['frequency translator', ['FREQ_TRANS_01']],
  ['downconverter', ['FREQ_TRANS_01']],
  ['lo_lock', ['FREQ_TRANS_01']],
  ['feed', ['FEED_HORN_01']],
  ['feed_horn', ['FEED_HORN_01']],
  ['horn', ['FEED_HORN_01']],
  ['reflector', ['PRIMARY_REFLECTOR_01']],
  ['primary_reflector', ['PRIMARY_REFLECTOR_01']],
  ['dish', ['PRIMARY_REFLECTOR_01']],
  ['surface', ['PRIMARY_REFLECTOR_01']],
  ['pedestal', ['PEDESTAL_01']],
  ['foundation', ['PEDESTAL_01']],
  ['spectrum', ['SPECTRUM_ANALYZER_01']],
  ['spectrum_analyzer', ['SPECTRUM_ANALYZER_01']],
  ['analyzer', ['SPECTRUM_ANALYZER_01']],
  ['signal_level', ['SPECTRUM_ANALYZER_01']],
  ['carrier_to_noise', ['SPECTRUM_ANALYZER_01']],
  ['noise_floor', ['SPECTRUM_ANALYZER_01']],
  ['bit_synchronizer', ['BIT_SYNC_01']],
  ['synchronizer', ['BIT_SYNC_01']],
  ['sync', ['BIT_SYNC_01']],
  ['frame_sync', ['BIT_SYNC_01']],
  ['decoder', ['DECODER_01']],
  ['fec', ['DECODER_01']],
  ['error correction', ['DECODER_01']],
  ['if_distribution', ['IF_DIST_01']],
  ['distribution', ['IF_DIST_01']],
  ['ifl', ['IFL_SWITCH_01']],
  ['ifl_switch', ['IFL_SWITCH_01']],
  ['fiber', ['IFL_SWITCH_01']],
  ['power', ['POWER_SUPPLY_01']],
  ['power_supply', ['POWER_SUPPLY_01']],
  ['psu', ['POWER_SUPPLY_01']],
  ['storage', ['STORAGE_SERVER_01']],
  ['storage_servers', ['STORAGE_SERVER_01']],
  ['server', ['STORAGE_SERVER_01']],
  ['raid', ['STORAGE_SERVER_01']],
  ['acu', ['ACU_01', 'ACU_COOLING_FAN_01']],
  ['antenna_control', ['ACU_01']],
  ['antenna control unit', ['ACU_01']],
  ['cpu_usage', ['ACU_01']],
  ['cabinet_temperature', ['ACU_01']],
  ['beacon', ['BEACON_RECEIVER_01']],
  ['beacon_receiver', ['BEACON_RECEIVER_01']],
  ['tracking', ['TRACKING_PROCESSOR_01']],
  ['tracking_processor', ['TRACKING_PROCESSOR_01']],
  ['switch_matrix', ['SWITCH_MATRIX_01']],
  ['matrix', ['SWITCH_MATRIX_01']],
  ['active_routes', ['SWITCH_MATRIX_01']],
];

export type WarrantyState = 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'UNKNOWN';

export interface WarrantyStatus {
  status: WarrantyState;
  daysRemaining: number;
  message: string;
  cssClass: 'active' | 'expiring' | 'expired' | 'unknown';
}

export interface EarlyFailure {
  yearsEarly: number;
  warrantyClaimEligible: boolean;
  message: string;
}

export type HealthBadgeState = 'good' | 'fair' | 'warning' | 'critical' | 'unknown';

export interface AgeHealth {
  percentage: number;
  status: HealthBadgeState;
  message: string;
}

/** "Aug 15, 2023". */
export function formatSpecDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** camelCase or snake_case to Title Case. */
export function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

export function getSpecification(data: EquipmentSpecifications, instanceId: string): EquipmentSpec | null {
  return data.installed_equipment[instanceId] || null;
}

/** mappings.subsystem_to_instance first, then the first instance whose subsystemPath contains the id. */
export function findBySubsystem(data: EquipmentSpecifications, subsystemId: string): EquipmentSpec | null {
  const mappings = data.mappings && data.mappings.subsystem_to_instance;
  if (mappings && mappings[subsystemId]) return getSpecification(data, mappings[subsystemId]);
  for (const id of Object.keys(data.installed_equipment)) {
    const spec = data.installed_equipment[id];
    if (spec.subsystemPath && spec.subsystemPath.includes(subsystemId)) return spec;
  }
  return null;
}

/** The keyword table (first keyword contained in the lower-cased name wins), then alphanumeric containment either way. */
export function findByName(data: EquipmentSpecifications, componentName: string | null | undefined): EquipmentSpec | null {
  if (!componentName) return null;
  const searchLower = componentName.toLowerCase();
  for (const [keyword, instanceIds] of NAME_KEYWORDS) {
    if (searchLower.includes(keyword)) {
      for (const instanceId of instanceIds) {
        const spec = data.installed_equipment[instanceId];
        if (spec) return spec;
      }
    }
  }
  const normalizedSearch = searchLower.replace(/[^a-z0-9]/g, '');
  for (const id of Object.keys(data.installed_equipment)) {
    const spec = data.installed_equipment[id];
    const normalizedName = spec.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedName.includes(normalizedSearch) || normalizedSearch.includes(normalizedName)) return spec;
  }
  return null;
}

/** EXPIRED, EXPIRING under 90 days, else ACTIVE ("Valid until <date>"); UNKNOWN without a warranty date. */
export function getWarrantyStatus(spec: EquipmentSpec | null, nowMs: number): WarrantyStatus {
  if (!spec || !spec.currentPart || !spec.currentPart.warrantyExpires) {
    return { status: 'UNKNOWN', daysRemaining: 0, message: 'Warranty info unavailable', cssClass: 'unknown' };
  }
  const warrantyDate = new Date(spec.currentPart.warrantyExpires).getTime();
  const daysRemaining = Math.ceil((warrantyDate - nowMs) / (1000 * 60 * 60 * 24));
  if (daysRemaining < 0) {
    return {
      status: 'EXPIRED',
      daysRemaining: 0,
      message: 'Warranty expired ' + Math.abs(daysRemaining) + ' days ago',
      cssClass: 'expired',
    };
  }
  if (daysRemaining < 90) {
    return { status: 'EXPIRING', daysRemaining, message: 'Expires in ' + daysRemaining + ' days', cssClass: 'expiring' };
  }
  return {
    status: 'ACTIVE',
    daysRemaining,
    message: 'Valid until ' + formatSpecDate(spec.currentPart.warrantyExpires),
    cssClass: 'active',
  };
}

/** Years before the expected end of life, and whether the failure falls inside the warranty; null when not early. */
export function checkEarlyFailure(spec: EquipmentSpec | null, failureMs: number): EarlyFailure | null {
  if (!spec || !spec.currentPart) return null;
  const expectedEOL = new Date(spec.currentPart.expectedEndOfLife).getTime();
  const warrantyExpires = new Date(spec.currentPart.warrantyExpires).getTime();
  if (failureMs < expectedEOL) {
    const yearsEarly = ((expectedEOL - failureMs) / (1000 * 60 * 60 * 24 * 365)).toFixed(1);
    const warrantyClaimEligible = failureMs < warrantyExpires;
    return {
      yearsEarly: parseFloat(yearsEarly),
      warrantyClaimEligible,
      message: warrantyClaimEligible
        ? 'Failing ' + yearsEarly + ' years early - WARRANTY CLAIM ELIGIBLE'
        : 'Failing ' + yearsEarly + ' years early - warranty expired',
    };
  }
  return null;
}

/** Age-based health: 100 minus the share of the install-to-EOL span elapsed; good ≥70, fair ≥40, warning ≥20, else critical. */
export function calculateAgeHealth(spec: EquipmentSpec | null, nowMs: number): AgeHealth {
  if (!spec || !spec.currentPart || !spec.operationalData) {
    return { percentage: 100, status: 'unknown', message: 'Health data unavailable' };
  }
  const installDate = new Date(spec.currentPart.installDate).getTime();
  const expectedEOL = new Date(spec.currentPart.expectedEndOfLife).getTime();
  const totalLifespan = expectedEOL - installDate;
  const elapsed = nowMs - installDate;
  const healthPercentage = Math.round(Math.max(0, 100 - (elapsed / totalLifespan) * 100));

  if (healthPercentage >= 70) return { percentage: healthPercentage, status: 'good', message: 'Good condition' };
  if (healthPercentage >= 40) return { percentage: healthPercentage, status: 'fair', message: 'Monitor closely' };
  if (healthPercentage >= 20) return { percentage: healthPercentage, status: 'warning', message: 'Plan replacement' };
  return { percentage: healthPercentage, status: 'critical', message: 'Replace soon' };
}

/** The spare part installed in this instance, from the inventory. */
export function getRelatedInventory(spec: EquipmentSpec | null, inventory: Pick<InventoryManager, 'getPart'> | null): InventoryPart | null {
  if (!inventory || !spec || !spec.currentPart) return null;
  return inventory.getPart(spec.currentPart.partNumber);
}

/** The Early Failure Alert card shows when the age health is below 50 and the part is before its EOL. */
export function earlyFailureAlert(spec: EquipmentSpec | null, nowMs: number): EarlyFailure | null {
  const health = calculateAgeHealth(spec, nowMs);
  const early = checkEarlyFailure(spec, nowMs);
  return early && health.percentage < 50 ? early : null;
}

/** Spare Parts Status card: in-stock at or above minStock, low-stock above zero, else out-of-stock. */
export function sparePartsCard(
  inventory: InventoryPart | null,
): { stockClass: 'in-stock' | 'low-stock' | 'out-of-stock' | 'unknown'; text: string } {
  if (!inventory) return { stockClass: 'unknown', text: 'Inventory data unavailable' };
  const stockClass = inventory.quantity >= inventory.minStock ? 'in-stock' : inventory.quantity > 0 ? 'low-stock' : 'out-of-stock';
  const text =
    inventory.quantity > 0
      ? inventory.quantity + ' in stock at ' + inventory.location
      : 'Out of stock - ' + inventory.leadTimeDays + ' day lead time';
  return { stockClass, text };
}

/** Newest first. */
export function sortedServiceHistory(spec: EquipmentSpec): ServiceHistoryEntry[] {
  return (spec.serviceHistory || []).slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

const HISTORY_TYPE_LABELS: Record<string, string> = {
  INSTALL: 'Installation',
  INSPECTION: 'Inspection',
  CLEANING: 'Cleaning',
  CALIBRATION: 'Calibration',
  REPAIR: 'Repair',
  REPLACE: 'Replacement',
  LUBRICATION: 'Lubrication',
  FIRMWARE: 'Firmware Update',
};

const HISTORY_ICONS: Record<string, string> = {
  INSTALL: 'fa-plus-circle',
  INSPECTION: 'fa-search',
  CLEANING: 'fa-broom',
  CALIBRATION: 'fa-sliders-h',
  REPAIR: 'fa-wrench',
  REPLACE: 'fa-exchange-alt',
  LUBRICATION: 'fa-oil-can',
  FIRMWARE: 'fa-microchip',
};

export function formatHistoryType(type: string): string {
  return HISTORY_TYPE_LABELS[type] || type;
}

export function getHistoryIcon(type: string): string {
  return HISTORY_ICONS[type] || 'fa-clipboard';
}

export function getHistoryIconClass(type: string): string {
  return HISTORY_TYPE_LABELS[type] ? type.toLowerCase() : 'default';
}

// -------------------------------------------------------------- readings tab

interface ReadingRange {
  normal: [number, number];
  warn: [number, number];
  unit: string;
  invert?: boolean;
  isStatus?: boolean;
}

/** The Readings tab's hard-coded ranges (not the sensor definitions). */
const READING_RANGES: Record<string, ReadingRange> = {
  fan_speed: { normal: [2800, 3200], warn: [2400, 3600], unit: 'RPM' },
  fan_temperature: { normal: [25, 50], warn: [20, 60], unit: '°C' },
  lna_temperature: { normal: [25, 45], warn: [20, 50], unit: '°C' },
  lna_gain: { normal: [55, 65], warn: [50, 70], unit: 'dB' },
  lna_noise_figure: { normal: [0.3, 0.8], warn: [0.2, 1.2], unit: 'dB', invert: true },
  hpa_temperature: { normal: [30, 60], warn: [25, 70], unit: '°C' },
  hpa_output_power: { normal: [100, 400], warn: [80, 450], unit: 'W' },
  hpa_efficiency: { normal: [35, 45], warn: [30, 50], unit: '%' },
  hpa_current: { normal: [10, 25], warn: [8, 30], unit: 'A' },
  antenna_az_current: { normal: [1, 5], warn: [0.5, 6], unit: 'A' },
  antenna_el_current: { normal: [1, 5], warn: [0.5, 6], unit: 'A' },
  antenna_vibration: { normal: [0, 2.5], warn: [0, 4.5], unit: 'mm/s' },
  antenna_az_pos: { normal: [0, 360], warn: [0, 360], unit: '°' },
  antenna_el_pos: { normal: [5, 90], warn: [0, 90], unit: '°' },
  modem_lock: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  modem_ber: { normal: [0, 1e-6], warn: [0, 1e-4], unit: '', invert: true },
  modem_eb_n0: { normal: [8, 15], warn: [5, 18], unit: 'dB' },
  waveguide_pressure: { normal: [0.5, 2.0], warn: [0.3, 2.5], unit: 'PSI' },
  waveguide_vswr: { normal: [1.0, 1.5], warn: [1.0, 2.0], unit: '', invert: true },
  ft_lo_lock: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  ft_output_level: { normal: [-10, 0], warn: [-15, 5], unit: 'dBm' },
  switch_port_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  switch_throughput: { normal: [1, 10], warn: [0.5, 10], unit: 'Gbps' },
  sa_signal_level: { normal: [-60, -30], warn: [-70, -25], unit: 'dBm' },
  sa_noise_floor: { normal: [-90, -70], warn: [-95, -65], unit: 'dBm' },
  acu_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  acu_cpu_usage: { normal: [10, 60], warn: [5, 80], unit: '%' },
  acu_cabinet_temp: { normal: [20, 35], warn: [15, 45], unit: '°C' },
  beacon_lock_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  beacon_signal_level: { normal: [-100, -70], warn: [-110, -65], unit: 'dBm' },
  tracking_processing_load: { normal: [20, 70], warn: [10, 85], unit: '%' },
  sync_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  frame_sync: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  slip_count: { normal: [0, 10], warn: [0, 50], unit: '' },
  decoder_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  corrected_errors: { normal: [0, 100], warn: [0, 500], unit: '/sec' },
  decoder_ber: { normal: [0, 1e-6], warn: [0, 1e-4], unit: '', invert: true },
  if_input_level: { normal: [-30, -10], warn: [-40, -5], unit: 'dBm' },
  if_distribution_gain: { normal: [18, 22], warn: [15, 25], unit: 'dB' },
  if_active_outputs: { normal: [6, 8], warn: [4, 8], unit: '' },
  ifl_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  active_paths: { normal: [2, 4], warn: [1, 4], unit: '' },
  fiber_power: { normal: [-10, 0], warn: [-15, 3], unit: 'dBm' },
  power_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  voltage_output: { normal: [47, 49], warn: [46, 50], unit: 'V' },
  psu_current_draw: { normal: [5, 20], warn: [2, 23], unit: 'A' },
  storage_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  storage_used: { normal: [10, 70], warn: [5, 85], unit: '%' },
  raid_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  write_rate: { normal: [1, 4], warn: [0.5, 4.5], unit: 'GB/s' },
  switch_matrix_status: { normal: [1, 1], warn: [0, 1], unit: '', isStatus: true },
  matrix_throughput: { normal: [5, 18], warn: [2, 18], unit: 'Gbps' },
  feed_horn_vswr: { normal: [1.0, 1.25], warn: [1.0, 1.5], unit: '', invert: true },
  surface_accuracy: { normal: [0, 0.6], warn: [0, 0.8], unit: 'mm RMS' },
  foundation_settlement: { normal: [0, 5], warn: [0, 10], unit: 'mm' },
};

/** Colour and "Normal: a-b unit" for a value, by exact sensor id then by underscore-stripped containment either way. */
export function determineReadingStatus(sensorId: string, value: number): { color: StatusColour; rangeText: string } {
  let range = READING_RANGES[sensorId];
  if (!range) {
    for (const key of Object.keys(READING_RANGES)) {
      if (
        sensorId.toLowerCase().includes(key.replace(/_/g, '').toLowerCase()) ||
        key.toLowerCase().includes(sensorId.replace(/_/g, '').toLowerCase())
      ) {
        range = READING_RANGES[key];
        break;
      }
    }
  }
  if (!range) return { color: 'green', rangeText: 'Range not configured' };
  const isNormal = value >= range.normal[0] && value <= range.normal[1];
  const isWarn = value >= range.warn[0] && value <= range.warn[1];
  const color: StatusColour = isNormal ? 'green' : isWarn ? 'yellow' : 'red';
  return { color, rangeText: 'Normal: ' + range.normal[0] + '-' + range.normal[1] + ' ' + range.unit };
}

/** Unit and precision by sensor id keyword. */
export function formatSensorValue(sensorId: string, value: number | string): string {
  const sensorLower = sensorId.toLowerCase();
  if (sensorLower.includes('status') || sensorLower.includes('lock') || sensorLower.includes('sync')) {
    if (typeof value === 'string') return value;
    if (value === 1) return 'LOCKED';
    if (value === 0) return 'UNLOCKED';
    return String(value);
  }
  if (typeof value !== 'number') return String(value);
  if (sensorLower.includes('temperature') || sensorLower.includes('temp')) return value.toFixed(1) + '°C';
  if (sensorLower.includes('speed') || sensorLower.includes('rpm')) return Math.round(value).toLocaleString() + ' RPM';
  if (sensorLower.includes('current')) return value.toFixed(2) + ' A';
  if (sensorLower === 'hpa_output_power' || sensorLower.includes('output_power')) return value.toFixed(1) + ' W';
  if (sensorLower.includes('level') || sensorLower.includes('dbm') || sensorLower.includes('fiber_power')) {
    return value.toFixed(1) + ' dBm';
  }
  if (sensorLower.includes('gain') || sensorLower.includes('noise_figure') || sensorLower.includes('eb_n0')) {
    return value.toFixed(2) + ' dB';
  }
  if (
    sensorLower.includes('efficiency') ||
    sensorLower.includes('usage') ||
    sensorLower.includes('used') ||
    sensorLower.includes('load')
  ) {
    return value.toFixed(1) + '%';
  }
  if (sensorLower.includes('voltage')) return value.toFixed(2) + ' V';
  if (sensorLower.includes('pos') || sensorLower.includes('azimuth') || sensorLower.includes('elevation')) {
    return value.toFixed(2) + '°';
  }
  if (sensorLower.includes('vibration')) return value.toFixed(2) + ' mm/s';
  if (sensorLower.includes('pressure')) return value.toFixed(2) + ' PSI';
  if (sensorLower.includes('vswr')) return value.toFixed(2) + ':1';
  if (sensorLower.includes('ber')) return value.toExponential(2);
  if (sensorLower.includes('throughput')) return value.toFixed(1) + ' Gbps';
  if (sensorLower.includes('write_rate')) return value.toFixed(1) + ' GB/s';
  if (
    sensorLower.includes('count') ||
    sensorLower.includes('outputs') ||
    sensorLower.includes('paths') ||
    sensorLower.includes('routes')
  ) {
    return Math.round(value).toString();
  }
  if (sensorLower.includes('corrected')) return Math.round(value) + '/sec';
  if (sensorLower.includes('accuracy') || sensorLower.includes('settlement')) return value.toFixed(2) + ' mm';
  return value.toFixed(2);
}

export type ReadingTrend = 'rising' | 'falling' | 'stable';

/** Newest three against oldest three of the (newest-first) readings: ±5 % change. */
export function calculateReadingTrend(readings: Pick<StoredReading, 'value'>[]): ReadingTrend {
  if (readings.length < 3) return 'stable';
  const recent = readings.slice(0, 3).reduce((sum, r) => sum + r.value, 0) / 3;
  const older = readings.slice(-3).reduce((sum, r) => sum + r.value, 0) / 3;
  const percentChange = ((recent - older) / older) * 100;
  if (percentChange > 5) return 'rising';
  if (percentChange < -5) return 'falling';
  return 'stable';
}

export function formatTrendLabel(trend: ReadingTrend): string {
  switch (trend) {
    case 'rising':
      return 'Rising';
    case 'falling':
      return 'Declining';
    default:
      return 'Stable';
  }
}

export function getTimeAgo(ms: number, nowMs: number): string {
  const seconds = Math.floor((nowMs - ms) / 1000);
  if (seconds < 60) return seconds + ' seconds ago';
  if (seconds < 3600) return Math.floor(seconds / 60) + ' minutes ago';
  if (seconds < 86400) return Math.floor(seconds / 3600) + ' hours ago';
  return Math.floor(seconds / 86400) + ' days ago';
}

export interface ReadingCard {
  sensorId: string;
  label: string;
  /** null when the store has no readings. */
  value: string | null;
  color: StatusColour | null;
  rangeText: string;
  trend: ReadingTrend | null;
  trendLabel: string;
  latestMs: number | null;
}

export interface ReadingsTab {
  cards: ReadingCard[];
  /** "Last updated: x ago" or "No recent data". */
  lastUpdated: string;
}

/** The Readings tab: one card per sensorId from the store's last 100 readings (newest first), trend over the last ten. */
export async function loadSensorReadings(
  sensorIds: string[],
  store: Pick<HistoricalDataStore, 'getReadings'>,
  nowMs: number,
): Promise<ReadingsTab> {
  const cards: ReadingCard[] = [];
  let latestTimestamp: number | null = null;
  for (const sensorId of sensorIds) {
    const readings = await store.getReadings(sensorId, { limit: 100 });
    if (readings.length === 0) {
      cards.push({
        sensorId,
        label: formatKey(sensorId),
        value: null,
        color: null,
        rangeText: 'No readings found',
        trend: null,
        trendLabel: '',
        latestMs: null,
      });
      continue;
    }
    readings.sort((a, b) => b.timestamp - a.timestamp);
    const latest = readings[0];
    if (latestTimestamp === null || latest.timestamp > latestTimestamp) latestTimestamp = latest.timestamp;
    const trend = calculateReadingTrend(readings.slice(0, 10));
    const status = determineReadingStatus(sensorId, latest.value);
    cards.push({
      sensorId,
      label: formatKey(sensorId),
      value: formatSensorValue(sensorId, latest.value),
      color: status.color,
      rangeText: status.rangeText,
      trend,
      trendLabel: formatTrendLabel(trend),
      latestMs: latest.timestamp,
    });
  }
  return {
    cards,
    lastUpdated: latestTimestamp !== null ? 'Last updated: ' + getTimeAgo(latestTimestamp, nowMs) : 'No recent data',
  };
}
